import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import { toast } from 'react-hot-toast';
import { ShieldExclamationIcon, MapPinIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { formatSpeed, formatDistance } from '../../utils/unitConversions';

// Fix default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const Container = styled.div`
  min-height: 100vh;
  background: ${theme.colors.background.primary};
  padding: 2rem;
  font-family: ${theme.typography.fontFamily.primary};
`;

const AlarmBanner = styled.div`
  background: ${theme.colors.error.main};
  color: ${theme.colors.error.contrast};
  padding: 1.5rem 2rem;
  border-radius: ${theme.borderRadius.lg};
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: ${theme.shadows.lg};
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.9; }
  }
`;

const AlarmInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AlarmTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  margin: 0;
  font-family: ${theme.typography.fontFamily.primary};
`;

const AlarmDetails = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  opacity: 0.9;
  margin-top: 0.25rem;
  font-family: ${theme.typography.fontFamily.primary};
`;

const DismissButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid white;
  padding: 0.75rem 1.5rem;
  border-radius: ${theme.borderRadius.md};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: ${theme.transitions.default};
  font-family: ${theme.typography.fontFamily.primary};
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const MapContainerStyled = styled.div`
  width: 100%;
  height: 600px;
  background: ${theme.colors.background.secondary};
  border: 2px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  overflow: hidden;
  position: relative;
  box-shadow: ${theme.shadows.xl};
  
  .leaflet-container {
    height: 100%;
    width: 100%;
    border-radius: ${theme.borderRadius.xl};
  }
`;

const InfoPanel = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 2rem;
  margin-top: 2rem;
  font-family: ${theme.typography.fontFamily.primary};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InfoLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  font-weight: ${theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: ${theme.typography.fontFamily.primary};
`;

const InfoValue = styled.div`
  font-size: ${theme.typography.fontSize.xl};
  color: ${theme.colors.text.primary};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: ${theme.typography.fontFamily.primary};
`;

const LiveIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${theme.colors.error.main};
  color: white;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  animation: blink 1s infinite;
  font-family: ${theme.typography.fontFamily.primary};
  margin-bottom: 1rem;
  
  @keyframes blink {
    0%, 50%, 100% { opacity: 1; }
    25%, 75% { opacity: 0.5; }
  }
`;

const OnGuardTracking = () => {
  const { vesselId, eventId } = useParams();
  const navigate = useNavigate();
  const { preferences } = useUserPreferences();
  const [vessel, setVessel] = useState(null);
  const [event, setEvent] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [homeLocation, setHomeLocation] = useState(null);
  const [activationLocation, setActivationLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const wsRef = useRef(null);

  useEffect(() => {
    loadTrackingData();
    setupLiveTracking();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [vesselId, eventId]);

  const loadTrackingData = async () => {
    try {
      // Load vessel info
      const vesselResponse = await api.get(`/api/vessels/${vesselId}`);
      const vesselData = vesselResponse.data.vessel || vesselResponse.data;
      setVessel(vesselData);
      
      // Set home location from vessel data
      if (vesselData.home_port_latitude && vesselData.home_port_longitude) {
        setHomeLocation({
          latitude: parseFloat(vesselData.home_port_latitude),
          longitude: parseFloat(vesselData.home_port_longitude)
        });
      }
      
      // Load alarm event
      const eventsResponse = await api.get(`/api/vessels/${vesselId}/onguard/events`);
      const targetEvent = eventsResponse.data.events.find(e => e.id === parseInt(eventId));
      setEvent(targetEvent);
      
      // Find the most recent activation event to get the location where OnGuard was turned on
      const activationEvent = eventsResponse.data.events.find(e => e.event_type === 'onguard_enabled');
      if (activationEvent && activationEvent.metadata) {
        const activationMeta = typeof activationEvent.metadata === 'string' 
          ? JSON.parse(activationEvent.metadata) 
          : activationEvent.metadata;
        
        if (activationMeta.activation_location) {
          setActivationLocation({
            latitude: parseFloat(activationMeta.activation_location.latitude),
            longitude: parseFloat(activationMeta.activation_location.longitude)
          });
          console.log('🛡️ OnGuard was activated at:', activationMeta.activation_location);
        }
      }
      
      if (targetEvent && targetEvent.metadata) {
        const metadata = typeof targetEvent.metadata === 'string' 
          ? JSON.parse(targetEvent.metadata) 
          : targetEvent.metadata;
        
        if (metadata.currentLocation) {
          setCurrentLocation({
            latitude: parseFloat(metadata.currentLocation.latitude),
            longitude: parseFloat(metadata.currentLocation.longitude),
            timestamp: targetEvent.created_at
          });
        }
      }
      
      // Also try to get current live position
      try {
        const liveResponse = await api.get(`/api/vessels/${vesselId}/live-position`);
        if (liveResponse.data && liveResponse.data.latitude && liveResponse.data.longitude) {
          setCurrentLocation({
            latitude: parseFloat(liveResponse.data.latitude),
            longitude: parseFloat(liveResponse.data.longitude),
            timestamp: liveResponse.data.timestamp,
            isLive: true
          });
        }
      } catch (liveError) {
        console.log('Could not get live position, using event location');
      }
      
    } catch (error) {
      console.error('Error loading tracking data:', error);
      toast.error('Failed to load tracking data');
    } finally {
      setLoading(false);
    }
  };

  const setupLiveTracking = () => {
    // Set up WebSocket for real-time position updates
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.hostname}/stream?vesselId=${vesselId}`;
    
    const ws = new WebSocket(wsUrl);
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'delta' && data['navigation.position']) {
          const posData = data['navigation.position'];
          setCurrentLocation({
            latitude: parseFloat(posData.value.latitude),
            longitude: parseFloat(posData.value.longitude),
            timestamp: posData.timestamp,
            isLive: true
          });
        }
      } catch (error) {
        console.error('Error processing WebSocket message:', error);
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    ws.onclose = () => {
      console.log('WebSocket connection closed');
    };
    
    wsRef.current = ws;
  };

  const handleDismissAlarm = async () => {
    if (!window.confirm('Dismiss the OnGuard alarm? This confirms the movement was authorized.')) {
      return;
    }
    
    try {
      await api.post(`/api/vessels/${vesselId}/onguard/dismiss-alarm`, { eventId });
      toast.success('Alarm dismissed successfully');
      navigate('/vessels');
    } catch (error) {
      console.error('Error dismissing alarm:', error);
      toast.error('Failed to dismiss alarm');
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in meters
  };

  if (loading || !preferences) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '4rem', color: theme.colors.text.secondary }}>
          Loading tracking data...
        </div>
      </Container>
    );
  }

  // Calculate distance from where OnGuard was activated, not from home port
  const referenceLocation = activationLocation || homeLocation;
  const distanceFromActivation = referenceLocation && currentLocation 
    ? calculateDistance(
        referenceLocation.latitude, 
        referenceLocation.longitude, 
        currentLocation.latitude, 
        currentLocation.longitude
      )
    : null;

  // Convert distance from meters to nautical miles, then format
  const distanceNM = distanceFromActivation ? distanceFromActivation / 1852 : 0;

  return (
    <Container>
      <AlarmBanner>
        <AlarmInfo>
          <ShieldExclamationIcon style={{ width: '3rem', height: '3rem' }} />
          <div>
            <AlarmTitle>🚨 OnGuard Security Alert</AlarmTitle>
            <AlarmDetails>
              {vessel?.vessel_name || 'Unknown Vessel'} - Unauthorized movement detected
            </AlarmDetails>
          </div>
        </AlarmInfo>
        <DismissButton onClick={handleDismissAlarm}>
          <XMarkIcon className="h-5 w-5" style={{ display: 'inline', marginRight: '0.5rem' }} />
          Dismiss Alarm
        </DismissButton>
      </AlarmBanner>

      <LiveIndicator>
        <div style={{ width: '8px', height: '8px', background: 'white', borderRadius: '50%' }} />
        {currentLocation?.isLive ? 'LIVE TRACKING ACTIVE' : 'ALARM TRIGGERED'}
      </LiveIndicator>

      <MapContainerStyled>
        {currentLocation && (
          <MapContainer 
            center={[currentLocation.latitude, currentLocation.longitude]} 
            zoom={15} 
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <TileLayer
              attribution='&copy; <a href="http://www.openseamap.org">OpenSeaMap</a> contributors'
              url="https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png"
            />
            
            {/* Current vessel position */}
            <Marker position={[currentLocation.latitude, currentLocation.longitude]}>
              <Popup>
                <strong>{vessel?.vessel_name || 'Unknown Vessel'}</strong><br />
                <strong style={{ color: theme.colors.error.main }}>🚨 ALARM ACTIVE</strong><br />
                Lat: {currentLocation.latitude.toFixed(6)}°<br />
                Lon: {currentLocation.longitude.toFixed(6)}°<br />
                {currentLocation.timestamp && (
                  <>Time: {new Date(currentLocation.timestamp).toLocaleString()}</>
                )}
              </Popup>
            </Marker>
            
            {/* Activation point (where OnGuard was turned on) or Home port */}
            {(activationLocation || homeLocation) && (
              <>
                <Marker 
                  position={activationLocation 
                    ? [activationLocation.latitude, activationLocation.longitude]
                    : [homeLocation.latitude, homeLocation.longitude]
                  }
                  icon={L.icon({
                    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
                    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
                    iconSize: [25, 41],
                    iconAnchor: [12, 41],
                    popupAnchor: [1, -34],
                    shadowSize: [41, 41]
                  })}
                >
                  <Popup>
                    <strong>{activationLocation ? '🛡️ OnGuard Activation Point' : 'Home Port'}</strong><br />
                    {activationLocation 
                      ? 'Security perimeter set here' 
                      : (vessel?.marina_location || 'Safe Location')}
                  </Popup>
                </Marker>
                
                {/* Geofence circle around activation point or home */}
                <Circle
                  center={activationLocation 
                    ? [activationLocation.latitude, activationLocation.longitude]
                    : [homeLocation.latitude, homeLocation.longitude]
                  }
                  radius={vessel?.onguard_geofence_radius || 10}
                  pathOptions={{ 
                    color: theme.colors.success.main, 
                    fillColor: theme.colors.success.main, 
                    fillOpacity: 0.1 
                  }}
                />
              </>
            )}
          </MapContainer>
        )}
      </MapContainerStyled>

      <InfoPanel>
        <h2 style={{ 
          fontSize: theme.typography.fontSize.xl, 
          fontWeight: theme.typography.fontWeight.semibold, 
          color: theme.colors.text.primary, 
          marginTop: 0,
          marginBottom: '1.5rem'
        }}>
          Alert Information
        </h2>
        
        <InfoGrid>
          <InfoItem>
            <InfoLabel>Vessel</InfoLabel>
            <InfoValue>{vessel?.vessel_name || 'Unknown'}</InfoValue>
          </InfoItem>
          
          <InfoItem>
            <InfoLabel>Alert Time</InfoLabel>
            <InfoValue>
              {event?.created_at ? new Date(event.created_at).toLocaleString() : 'Unknown'}
            </InfoValue>
          </InfoItem>
          
          <InfoItem>
            <InfoLabel>Distance from {activationLocation ? 'Activation Point' : 'Home'}</InfoLabel>
            <InfoValue style={{ color: theme.colors.error.main }}>
              {distanceFromActivation 
                ? formatDistance(distanceNM, preferences.distance_unit, 0)
                : 'Calculating...'}
            </InfoValue>
          </InfoItem>
          
          <InfoItem>
            <InfoLabel>Speed</InfoLabel>
            <InfoValue>
              {event?.speed_knots 
                ? formatSpeed(parseFloat(event.speed_knots), preferences.speed_unit, 1)
                : 'Unknown'}
            </InfoValue>
          </InfoItem>
          
          {currentLocation && (
            <>
              <InfoItem>
                <InfoLabel>Current Latitude</InfoLabel>
                <InfoValue>{currentLocation.latitude.toFixed(6)}°</InfoValue>
              </InfoItem>
              
              <InfoItem>
                <InfoLabel>Current Longitude</InfoLabel>
                <InfoValue>{currentLocation.longitude.toFixed(6)}°</InfoValue>
              </InfoItem>
            </>
          )}
        </InfoGrid>
      </InfoPanel>
    </Container>
  );
};

export default OnGuardTracking;
